import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@mui/material";
import classes from "./ReadMoreText.module.scss";

interface ReadMoreTextProps {
    text?: string;
    maxLines?: number;
    className?: string;
}

const ReadMoreText: React.FC<ReadMoreTextProps> = ({ text, maxLines = 3, className }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
        const checkOverflow = () => {
            const el = textRef.current;
            if (!el) return;

            // Store full height first
            el.style.maxHeight = "none";
            const fullTextHeight = el.scrollHeight;

            // Get computed line height
            const computedStyle = window.getComputedStyle(el);
            const lineHeight = parseInt(computedStyle.lineHeight);

            // Set max height based on line height and max lines
            const maxHeight = lineHeight * maxLines;
            el.style.maxHeight = isExpanded ? "none" : `${maxHeight}px`;

            // Check if content overflows max height
            setIsOverflowing(fullTextHeight > maxHeight);
        };

        checkOverflow();

        // Add resize listener incase screen size or orientation changes
        window.addEventListener("resize", checkOverflow);

        // Removing resize listener when component unmounts
        return () => window.removeEventListener("resize", checkOverflow);
    }, [isExpanded]);

    const toggleExpanded = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsExpanded(true);
    };

    return (
        <>
            <Typography
                ref={textRef}
                variant="body2"
                color="text.secondary"
                className={`${classes.textContent} ${className ?? ""}`}
                sx={{
                    whiteSpace: "pre-line",
                    overflow: "hidden",
                    transition: "max-height 0.3s ease",
                }}
            >
                {text}
            </Typography>

            {isOverflowing && (
                <span onClick={toggleExpanded} className={classes.readMoreButton}>
                    {!isExpanded && "...more"}
                </span>
            )}
        </>
    );
};

export default ReadMoreText;
